import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearLocalStorage } from '../utils/localStorage';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      clearLocalStorage();
      setMessage('All data cleared successfully!');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  const handleResetData = () => {
    if (window.confirm('Reset all data to default values?')) {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Settings</h1>
          <p className="text-gray-600 mb-6">
            Manage application settings and data
          </p>

          {message && (
            <div className="mb-4 p-4 rounded bg-green-100 text-green-700">
              {message}
            </div>
          )}

          {/* System Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              System Information
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-700">Application:</span>
                  <span className="text-gray-600 ml-2">JobMatch Platform</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Version:</span>
                  <span className="text-gray-600 ml-2">1.0.0</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Environment:</span>
                  <span className="text-gray-600 ml-2">Development</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Data Store:</span>
                  <span className="text-gray-600 ml-2">LocalStorage</span>
                </div>
              </div>
            </div>
          </div>

          {/* Data Management */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Data Management
            </h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">
                  View Storage Usage
                </h3>
                <p className="text-blue-800 text-sm mb-3">
                  Check how much LocalStorage is being used
                </p>
                <button
                  onClick={() => {
                    const usage = JSON.stringify(localStorage).length;
                    alert(`Storage Usage: ${(usage / 1024).toFixed(2)} KB`);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Check Usage
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-900 mb-2">
                  Reset Data
                </h3>
                <p className="text-yellow-800 text-sm mb-3">
                  Reload the application to reset data to default values
                </p>
                <button
                  onClick={handleResetData}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
                >
                  Reset Data
                </button>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-900 mb-2">
                  Clear All Data
                </h3>
                <p className="text-red-800 text-sm mb-3">
                  Permanently delete all data from LocalStorage. This action cannot be undone.
                </p>
                <button
                  onClick={handleClearData}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Clear All Data
                </button>
              </div>
            </div>
          </div>

          {/* User Management */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              User Management
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {JSON.parse(localStorage.getItem('jobSeekers') || '[]').length}
                  </div>
                  <div className="text-gray-600 text-sm">Job Seekers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {JSON.parse(localStorage.getItem('employers') || '[]').length}
                  </div>
                  <div className="text-gray-600 text-sm">Employers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {JSON.parse(localStorage.getItem('companies') || '[]').length}
                  </div>
                  <div className="text-gray-600 text-sm">Companies</div>
                </div>
              </div>
            </div>
          </div>

          {/* Application Stats */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Application Statistics
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {JSON.parse(localStorage.getItem('reviews') || '[]').length}
                  </div>
                  <div className="text-gray-600 text-sm">Total Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {JSON.parse(localStorage.getItem('aiMatches') || '[]').length}
                  </div>
                  <div className="text-gray-600 text-sm">AI Matches</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
